const reasons = [
  {
    icon: 'ri-focus-3-line',
    title: 'Thorough & Detail-Oriented',
    description:
      'Every inspection is conducted with care. We don&apos;t rush — we look closely at every accessible area so nothing important is overlooked.',
  },
  {
    icon: 'ri-chat-check-line',
    title: 'Clear Communication',
    description:
      'We explain what we find in plain language. No intimidating jargon, just straightforward information you can actually use.',
  },
  {
    icon: 'ri-calendar-2-line',
    title: 'Easy Scheduling',
    description:
      'Booking is simple and flexible. We work around your timeline so you can move forward without unnecessary delays.',
  },
  {
    icon: 'ri-map-pin-2-line',
    title: 'Local Northern Michigan Service',
    description:
      'We live and work in this community. We know Northern Michigan homes and are invested in the people we serve Up North.',
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'Honest, Straightforward Process',
    description:
      'No upselling, no exaggeration. You get an honest assessment of the property — exactly what you need to make a confident decision.',
  },
];

const steps = [
  {
    number: '01',
    icon: 'ri-calendar-check-line',
    title: 'Schedule Your Inspection',
    description: 'Contact us by phone, email, or our online form. We&apos;ll find a time that works for your schedule.',
  },
  {
    number: '02',
    icon: 'ri-home-gear-line',
    title: 'We Inspect the Property',
    description: 'Our thorough on-site inspection covers every accessible area of the home from foundation to roof.',
  },
  {
    number: '03',
    icon: 'ri-file-list-2-line',
    title: 'You Get Clear Findings',
    description: 'Receive your detailed, easy-to-read report promptly — giving you actionable information to move forward confidently.',
  },
];

export default function WhyChooseUs() {
  return (
    <section id="why" className="py-20 md:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Why Choose Us */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="block w-8 h-0.5 bg-accent"></span>
            <span className="text-accent font-semibold text-sm tracking-widest uppercase">Why Us</span>
            <span className="block w-8 h-0.5 bg-accent"></span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy leading-tight mb-4">
            Why Choose Precision North
          </h2>
          <p className="text-gray-500 text-base leading-relaxed">
            We&apos;re not just checking boxes. We&apos;re giving you the reliable information you need to make one of the biggest decisions of your life.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-20">
          {reasons.map((reason) => (
            <div key={reason.title} className="flex items-start gap-4 p-5 rounded-lg border border-gray-100 bg-surface hover:border-accent/30 transition-colors">
              <div className="w-10 h-10 flex items-center justify-center bg-navy rounded-md flex-shrink-0">
                <i className={`${reason.icon} text-white text-xl`}></i>
              </div>
              <div>
                <h3 className="font-heading font-semibold text-navy text-base mb-1">{reason.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: reason.description }} />
              </div>
            </div>
          ))}
          {/* Placeholder sixth slot: Testimonial teaser */}
          <div className="flex flex-col items-start p-5 rounded-lg bg-navy text-white sm:col-span-2 lg:col-span-1">
            <div className="flex mb-3">
              {[1,2,3,4,5].map((s) => (
                <div key={s} className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-amber-400 text-sm"></i>
                </div>
              ))}
            </div>
            <p className="text-white/80 text-sm italic leading-relaxed mb-4">
              &ldquo;Client testimonials and reviews will appear here as Precision North builds its reputation in Northern Michigan. Check back soon!&rdquo;
            </p>
            <p className="text-accent-light text-xs font-semibold">Building Trust One Inspection at a Time</p>
          </div>
        </div>

        {/* Process Section */}
        <div className="relative bg-navy rounded-xl px-6 md:px-12 py-12 overflow-hidden">
          {/* Background Texture */}
          <div className="absolute inset-0 opacity-5">
            <img
              src="https://public.readdy.ai/ai/img_res/069f5e673aa7fffe7d5fb5fd9f26a2a7.jpg"
              alt=""
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="relative z-10">
            <div className="text-center mb-10">
              <p className="text-accent-light font-semibold text-sm tracking-widest uppercase mb-3">Simple Process</p>
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-white">How It Works</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
              {steps.map((step, idx) => (
                <div key={step.number} className="flex flex-col items-center text-center relative">
                  {/* Connector line */}
                  {idx < steps.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-[calc(50%+2.5rem)] right-0 h-px bg-white/15"></div>
                  )}
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/10 border border-white/20 mb-4 relative">
                    <i className={`${step.icon} text-2xl text-accent-light`}></i>
                    <span className="absolute -top-1 -right-1 w-6 h-6 bg-accent rounded-full text-white text-xs font-bold flex items-center justify-center">
                      {idx + 1}
                    </span>
                  </div>
                  <h4 className="font-heading font-semibold text-white text-base mb-2">{step.title}</h4>
                  <p className="text-white/65 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: step.description }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
